/**
 * Lightweight document-type classifier for the docpic flow.
 *
 * The docpic screen already OCRs the captured document for its legibility gate,
 * so we get the page text "for free". {@link classifyDocument} scores that text
 * against a table of keyword signatures for the common Brazilian documents (plus
 * a couple of generic types) and returns the best match — no model, no network.
 *
 * Matching is accent- and case-insensitive: both the text and the keywords are
 * normalised (uppercased, diacritics stripped) so an English OCR pass over
 * Portuguese text still hits, and the keyword fragments are chosen to survive
 * the odd misread character. It is a heuristic, not a guarantee — extend the
 * {@link SIGNATURES} table to teach it new documents.
 */

/** A document type and the keyword fragments that identify it. */
export interface DocSignature {
  /** Short stable key, e.g. `cnh`. */
  type: string;
  /** Human-readable label shown in the UI. */
  label: string;
  /** `[fragment, weight]` pairs — fragments are normalised (UPPER, no accents). */
  keywords: [string, number][];
}

/** The outcome of classifying one document's text. */
export interface Classification {
  type: string;
  label: string;
  /** Summed weight of the matched keyword fragments. */
  score: number;
  /** Which fragments matched (for transparency / display). */
  matched: string[];
  /** Bucketed confidence derived from the score. */
  confidence: 'high' | 'medium' | 'low';
}

/**
 * Keyword signatures, strongest fragments weighted highest. Fragments are kept
 * short and distinctive so they survive OCR noise (e.g. `HABILITA` rather than
 * the full `CARTEIRA NACIONAL DE HABILITACAO`). Shared phrases like
 * `REPUBLICA FEDERATIVA DO BRASIL` are deliberately omitted as they don't
 * discriminate between document types.
 */
const SIGNATURES: DocSignature[] = [
  {
    type: 'cnh',
    label: 'CNH — Carteira Nacional de Habilitação',
    keywords: [
      ['HABILITA', 3],
      ['CARTEIRA NACIONAL', 2],
      ['PERMISSAO PARA DIRIGIR', 2],
      ['DETRAN', 1],
      ['CATEGORIA', 0.5],
    ],
  },
  {
    type: 'cin',
    label: 'CIN — Carteira de Identidade Nacional',
    keywords: [
      ['IDENTIDADE NACIONAL', 3],
      ['CIN', 1],
    ],
  },
  {
    type: 'rg',
    label: 'RG — Carteira de Identidade',
    keywords: [
      ['REGISTRO GERAL', 3],
      ['CARTEIRA DE IDENTIDADE', 2],
      ['SECRETARIA DE SEGURANCA', 1.5],
      ['FILIACAO', 0.5],
    ],
  },
  {
    type: 'cpf',
    label: 'CPF — Cadastro de Pessoas Físicas',
    keywords: [
      ['CADASTRO DE PESSOAS', 3],
      ['RECEITA FEDERAL', 1.5],
      ['MINISTERIO DA FAZENDA', 1],
    ],
  },
  {
    type: 'passaporte',
    label: 'Passaporte',
    keywords: [
      ['PASSAPORTE', 3],
      ['PASSPORT', 2],
      ['RELACOES EXTERIORES', 1.5],
    ],
  },
  {
    type: 'titulo-eleitor',
    label: 'Título de Eleitor',
    keywords: [
      ['JUSTICA ELEITORAL', 3],
      ['TITULO DE ELEITOR', 3],
      ['ELEITOR', 1],
      ['SECAO', 0.3],
      ['ZONA', 0.3],
    ],
  },
  {
    type: 'ctps',
    label: 'CTPS — Carteira de Trabalho',
    keywords: [
      ['CARTEIRA DE TRABALHO', 3],
      ['PREVIDENCIA', 1.5],
      ['CTPS', 1],
    ],
  },
  {
    type: 'nota-fiscal',
    label: 'Nota Fiscal',
    keywords: [
      ['NOTA FISCAL', 3],
      ['DANFE', 2],
      ['DOCUMENTO AUXILIAR', 1],
    ],
  },
  {
    type: 'comprovante',
    label: 'Comprovante de Residência / Fatura',
    keywords: [
      ['FATURA', 2],
      ['VENCIMENTO', 1],
      ['CONTA CONTRATO', 1.5],
      ['KWH', 1],
    ],
  },
];

const UNKNOWN: Classification = {
  type: 'unknown',
  label: 'Unknown document',
  score: 0,
  matched: [],
  confidence: 'low',
};

/** Classify a document from its OCR text. Returns the best-scoring signature. */
export function classifyDocument(text: string): Classification {
  const norm = normalize(text);
  if (!norm) return UNKNOWN;

  let best: Classification = UNKNOWN;
  for (const sig of SIGNATURES) {
    let score = 0;
    const matched: string[] = [];
    for (const [fragment, weight] of sig.keywords) {
      if (norm.includes(fragment)) {
        score += weight;
        matched.push(fragment);
      }
    }
    if (score > best.score) {
      best = { type: sig.type, label: sig.label, score, matched, confidence: bucket(score) };
    }
  }
  return best.score > 0 ? best : UNKNOWN;
}

/** type-key → human label, derived from the signatures (single source of truth). */
export const DOC_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  SIGNATURES.map((s) => [s.type, s.label]),
);

/** Acronyms that should stay uppercase when formatting a type key. */
const TYPE_ACRONYMS = new Set(['cin', 'cnh', 'rg', 'cpf', 'ctps', 'cnpj', 'qr']);

/**
 * Human label for a type key. Known keys use their signature label; otherwise a
 * trained key like `cin-back` / `cnh-digital` is formatted as "CIN — Back" /
 * "CNH — Digital" (first segment = document, rest = the side/variant).
 */
export function documentTypeLabel(type: string): string {
  if (DOC_TYPE_LABELS[type]) return DOC_TYPE_LABELS[type];
  if (type === 'unknown') return 'Unknown document';
  const parts = type.split('-').map((p) =>
    TYPE_ACRONYMS.has(p) ? p.toUpperCase() : p.charAt(0).toUpperCase() + p.slice(1),
  );
  return parts.length > 1 ? `${parts[0]} — ${parts.slice(1).join(' ')}` : parts[0];
}

/** A document-type prediction for the UI, from either the model or keywords. */
export interface DocPrediction {
  type: string;
  label: string;
  confidence: 'high' | 'medium' | 'low';
  /** Which classifier produced this. */
  source: 'model' | 'keywords';
  /** Per-expert top votes (model source only), for transparency. */
  experts?: { modality: string; type: string; prob: number }[];
}

/** Wrap the keyword classifier as a {@link DocPrediction} (the fallback path). */
export function keywordPrediction(text: string): DocPrediction {
  const c = classifyDocument(text);
  return { type: c.type, label: c.label, confidence: c.confidence, source: 'keywords' };
}

/** Bucket a raw score into a confidence band. */
function bucket(score: number): Classification['confidence'] {
  if (score >= 3) return 'high';
  if (score >= 1.5) return 'medium';
  return 'low';
}

/** Uppercase, strip diacritics, and collapse whitespace for robust matching. */
function normalize(s: string): string {
  return s
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toUpperCase()
    .replace(/\s+/g, ' ')
    .trim();
}
